import { TFunction } from "i18next";
import { ContractData } from "@/pages/contracts/types";
import { getCommittedRevenue } from "@/pages/contracts/committedRevenue";
import { parseLocaleNumericInput, reducePrecision } from "@/utils/numbers";
import { MONETARY_INPUT_PATTERN } from "@/hooks/useInputWithValidation";
import { MetricRow } from "./types";

export const parseInputToNumber = (raw: string): number | null => {
  const trimmed = raw.trim();
  if (trimmed === "") return null;
  // Reads the separators by the viewer's locale. This modal's inputs are plain
  // <Input>s rather than useInputWithValidation, so it needs the parse of its
  // own: stripping every comma stored a German user's 25000,50 as 2500050 on
  // recognized ARR/MRR/ACV/TCV, the same defect on a second path.
  // Why this guard exists: the modal's inputs are plain <Input>s with no
  // keystroke gate, so anything typeable arrives here, and `Number()` accepts
  // more than a money field should — without the check below, "0x1F" would save
  // as 31, "0b101" as 5 and "1e6" as a million, each passing the finite test as
  // a plausible figure. Pre-existing, and this is the only chokepoint the modal
  // has. Reuses the hook's own pattern rather than a second copy of it, so the
  // two cannot drift.
  if (!MONETARY_INPUT_PATTERN.test(trimmed)) return null;

  const cleaned = parseLocaleNumericInput(trimmed);
  // Re-check emptiness AFTER parsing, not just before: a separator-only entry
  // reduces to "" once its grouping is dropped, and `Number("")` is 0 — so a
  // stray comma would otherwise register as a recognized value of zero rather
  // than as no value at all.
  if (cleaned === "") return null;
  const n = Number(cleaned);
  return Number.isFinite(n) ? n : null;
};

export const formatForInput = (value: number | null | undefined): string => {
  if (value == null) return "";
  return reducePrecision(value, 0).toString();
};

export const isModified = (
  recognized: number | null,
  calculated: number | null
) => {
  if (recognized == null || calculated == null) return false;
  return reducePrecision(recognized, 0) !== reducePrecision(calculated, 0);
};

export const buildMetricRows = (
  contract: ContractData,
  isArr: boolean,
  t: TFunction,
  showCommittedValues = true
): MetricRow[] => {
  const recurringRecognized = isArr ? contract.arrLocal : contract.mrrLocal;
  const recurringCalculated =
    (isArr ? contract.calculatedArrLocal : contract.calculatedMrrLocal) ??
    recurringRecognized;
  const committedRevenue = getCommittedRevenue(contract, isArr);
  const committedRecognized = committedRevenue.recognizedLocal;
  // The Calculated column falls back to the recognized value when the contract
  // has no service-derived figure, so the row reads as unmodified rather than "—".
  const committedCalculated =
    committedRevenue.calculatedLocal ?? committedRecognized;

  const committedRows: MetricRow[] = showCommittedValues
    ? [
        {
          key: "committed",
          label: isArr ? t("contractTerms.carr") : t("contractTerms.cmrr"),
          recognizedField: isArr ? "carrLocal" : "cmrrLocal",
          recognizedValue: committedRecognized,
          calculatedValue: committedCalculated
        }
      ]
    : [];

  return [
    {
      key: "recurring",
      label: isArr ? t("contractTerms.arr") : t("contractTerms.mrr"),
      recognizedField: isArr ? "arrLocal" : "mrrLocal",
      recognizedValue: recurringRecognized,
      calculatedValue: recurringCalculated
    },
    ...committedRows,
    {
      key: "acv",
      label: t("contractTerms.acv"),
      recognizedField: "annualContractValueLocal",
      recognizedValue: contract.annualContractValueLocal,
      calculatedValue: contract.calculatedAnnualContractValueLocal ?? contract.annualContractValueLocal
    },
    {
      key: "tcv",
      label: t("contractTerms.tcv"),
      recognizedField: "totalContractValueLocal",
      recognizedValue: contract.totalContractValueLocal,
      calculatedValue: contract.calculatedTotalContractValueLocal ?? contract.totalContractValueLocal
    }
  ];
};
